import { Trip, MultiLegTrip, LegacyTrip } from '@/types/trip';

/**
 * Generates a stable-ish unique id (time + random). Pure helper with no side effects.
 */
export const generateId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

function isMultiLegTrip(trip: Trip): trip is MultiLegTrip {
  return 'legs' in trip && Array.isArray(trip.legs);
}

function isLegacyTrip(trip: Trip): trip is LegacyTrip {
  return 'inicio' in trip && 'fin' in trip && !('legs' in trip);
}

/**
 * Ensures every trip has an `id`, generating one when missing. Pure function.
 */
export function ensureIds(trips: Trip[]): Trip[] {
  return trips.map((t) => {
    if ('id' in t && typeof t.id === 'string') {
      return t;
    }
    return { ...t, id: generateId() };
  });
}

/**
 * Removes derived fields before persistence (pure). Keeps only raw user input per data rules.
 */
export function stripCalculated(trips: Trip[]): Trip[] {
  return trips.map((t) => {
    const id = 'id' in t ? t.id : undefined;
    const notas = 'notas' in t ? { notas: t.notas } : {};

    if (isMultiLegTrip(t)) {
      return {
        ...notas,
        id,
        legs: t.legs.map((l) => ({ inicio: l.inicio, fin: l.fin, tipo: l.tipo, region: l.region })),
      } as Trip;
    }

    if (isLegacyTrip(t)) {
      return {
        id,
        inicio: t.inicio,
        fin: t.fin,
        tipo: t.tipo,
      } as Trip;
    }

    // Fallback for any other shape
    return t;
  });
}

/**
 * Builds a deterministic key for a trip when no `id` exists (pure helper).
 */
export function tripKey(t: Trip): string {
  if (isMultiLegTrip(t)) {
    return JSON.stringify(t.legs.map((l) => ({ inicio: l.inicio, fin: l.fin })));
  }
  if (isLegacyTrip(t)) {
    return JSON.stringify({ inicio: t.inicio, fin: t.fin });
  }
  // Fallback
  return JSON.stringify(t);
}

/**
 * Finds a trip index using id when available, otherwise using structural equality fallback.
 */
export function findTripIndex(target: Trip, collection: Trip[], localIdx?: number): number {
  const directIdx = collection.indexOf(target);
  if (directIdx !== -1) {
    return directIdx;
  }

  const id = 'id' in target ? target.id : undefined;
  if (id) {
    const byId = collection.findIndex((trip) => 'id' in trip && trip.id === id);
    if (byId !== -1) {
      return byId;
    }
  }

  const key = tripKey(target);
  if (typeof localIdx === 'number') {
    let occurrence = 0;
    for (let i = 0; i < collection.length; i += 1) {
      if (tripKey(collection[i]) === key) {
        if (occurrence === localIdx) {
          return i;
        }
        occurrence += 1;
      }
    }
    return -1;
  }

  return collection.findIndex((trip) => tripKey(trip) === key);
}

// Centralized persist: ensure ids, strip calculated fields, then call storage layer
export default { generateId, ensureIds, stripCalculated, tripKey, findTripIndex };
